/** 画面全体の集計期間。年度は7月始まりの会計年度（airCloset は6月期決算） */
export type Period = 'month' | 'quarter' | 'year';

/**
 * 週次まで刻めるセクションが取りうる期間。
 * CS 系3指標（日次データを持つ）と CRM セクション（登録週コホート2本 + 退会者数の週次）。
 * 画面全体トグルには週次を含めず、この型でのみ 'week' を許す。
 */
export type CsPeriod = Period | 'week';

interface PeriodToggleProps {
  value: Period;
  onChange: (period: Period) => void;
}

/** @graph-connects none */
const OPTIONS: { value: Period; label: string }[] = [
  { value: 'month', label: '月次' },
  { value: 'quarter', label: '四半期' },
  { value: 'year', label: '年度' },
];

/** @graph-connects none */
export const PERIOD_LABEL: Record<Period, string> = {
  month: '月次',
  quarter: '四半期',
  year: '年度',
};

interface ToggleButtonProps {
  label: string;
  selected: boolean;
  onClick: () => void;
}

/** @graph-connects none */
function ToggleButton({ label, selected, onClick }: ToggleButtonProps) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className="px-3 py-1 text-xs font-medium rounded-md transition-colors"
      style={{
        background: selected ? 'var(--color-bg-surface)' : 'transparent',
        color: selected ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
        boxShadow: selected ? '0 1px 2px rgb(0 0 0 / 0.08)' : 'none',
      }}
    >
      {label}
    </button>
  );
}

interface ToggleGroupProps {
  label: string;
  children: React.ReactNode;
}

/** @graph-connects none */
function ToggleGroup({ label, children }: ToggleGroupProps) {
  return (
    <div
      role="group"
      aria-label={label}
      className="inline-flex rounded-lg p-0.5"
      style={{ background: 'var(--color-bg-hover)', border: '1px solid var(--color-border)' }}
    >
      {children}
    </div>
  );
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business ダッシュボードの集計期間（月次/四半期/年度）を切り替えるトグル
 * @graph-connects none
 */
export function PeriodToggle({ value, onChange }: PeriodToggleProps) {
  return (
    <ToggleGroup label="集計期間の切り替え">
      {OPTIONS.map((option) => (
        <ToggleButton
          key={option.value}
          label={option.label}
          selected={option.value === value}
          onClick={() => {
            onChange(option.value);
          }}
        />
      ))}
    </ToggleGroup>
  );
}

/** @graph-connects none */
const CRM_OPTIONS: { value: CsPeriod; label: string }[] = [
  { value: 'week', label: '週次' },
  { value: 'month', label: '月次' },
  { value: 'quarter', label: '四半期' },
  { value: 'year', label: '年度' },
];

interface CrmPeriodToggleProps {
  value: CsPeriod;
  onChange: (period: CsPeriod) => void;
}

/**
 * CRM｜ロイヤリティ改善セクション専用の期間トグル。**セクションの3枚をまとめて切り替える。**
 *
 * 3枚とも週次を持てるのでこの位置に置ける（退会者数は ac_records の日次行を週で合計、
 * 成功率2本は登録週コホートがネイティブ粒度）。カードごとの独立トグルは
 * 2026-09-25 に廃止した。並べて比較するのに粒度が揃っていない方が不便だったため。
 *
 * ⚠️ 週次の**意味は指標で違う**（成功率2本＝登録週 / 退会者数＝退会の発生週）。
 * X軸ラベルで出し分けているので、トグルを共通化しても軸の意味は混ざらない。
 *
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business CRMロイヤリティ改善セクションの集計期間（週次/月次/四半期/年度）を一括で切り替えるトグル
 * @graph-connects none
 */
export function CrmPeriodToggle({ value, onChange }: CrmPeriodToggleProps) {
  return (
    <ToggleGroup label="CRM ロイヤリティ改善の集計期間の切り替え">
      {CRM_OPTIONS.map((option) => (
        <ToggleButton
          key={option.value}
          label={option.label}
          selected={option.value === value}
          onClick={() => {
            onChange(option.value);
          }}
        />
      ))}
    </ToggleGroup>
  );
}

interface CsPeriodToggleProps {
  value: CsPeriod;
  onChange: (period: CsPeriod) => void;
}

/** @graph-connects none */
const CS_OPTIONS: { value: CsPeriod; label: string }[] = [
  { value: 'week', label: '週次' },
  { value: 'month', label: '月次' },
  { value: 'quarter', label: '四半期' },
];

/**
 * CS セクション専用の期間トグル。
 * 週次・月次・四半期の3択。週次は CS 系3指標だけが日次データを持つため刻める。
 * 崩れは週単位で先に見えるため（繁忙期は入りの1週で落ちて数週かけて戻る）、週次を既定にしている。
 *
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business CS対応品質セクションの集計期間（週次/月次/四半期）を切り替えるトグル
 * @graph-connects none
 */
export function CsPeriodToggle({ value, onChange }: CsPeriodToggleProps) {
  return (
    <ToggleGroup label="CS対応品質の集計期間の切り替え">
      {CS_OPTIONS.map((option) => (
        <ToggleButton
          key={option.value}
          label={option.label}
          selected={option.value === value}
          onClick={() => {
            onChange(option.value);
          }}
        />
      ))}
    </ToggleGroup>
  );
}
